/**
 * HOS v4.3 — Form-mode (5-act narrative) runner.
 *
 * One act visible at a time. Per-act 6-minute server-side clock.
 * On submit → server advances to next act or marks completed.
 * Uses the same telemetry pattern as TA (TAResponseField).
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Send, AlertTriangle, Clock, Save } from "lucide-react";
import { resolveBackendBase } from "../../lib/api";
import { secondsUntil, fmtClock } from "../ta/taHelpers";
import TAResponseField from "../ta/TAResponseField";

const API_URL = `${resolveBackendBase()}/api/v2`;

const ACT_TITLE_FALLBACK = {
    1: "Act 1: Three Fires",
    2: "Act 2: The Data Lies",
    3: "Act 3: The Ethical Trap",
    4: "Act 4: Everything Breaks",
    5: "Act 5: The Reckoning",
};

export default function HOSFormRunner({ state, onAdvance }) {
    const actNum = state?.hos_active_section;
    const sections = (state?.template?.content?.content || []).filter(s => s.type === "section");
    const section = sections.find((s) => s.section_number === actNum) || sections[actNum - 1];
    const actState = state?.hos_state?.[`act${actNum}`] || {};
    const items = section?.items || [];

    const [responses, setResponses] = useState(() => state?.saved_responses || {});
    const telemetryRef = useRef({});
    const [savingState, setSavingState] = useState("idle");
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [secsLeft, setSecsLeft] = useState(() => secondsUntil(actState.deadline_at));

    useEffect(() => {
        const id = setInterval(() => setSecsLeft(secondsUntil(actState.deadline_at)), 1000);
        return () => clearInterval(id);
    }, [actState.deadline_at]);

    // Debounced auto-save
    useEffect(() => {
        if (!Object.keys(telemetryRef.current).length) return;
        if (actState.locked) return;
        const t = setTimeout(async () => {
            const payload = Object.values(telemetryRef.current);
            setSavingState("saving");
            try {
                await axios.post(`${API_URL}/assessment/hos/${state.token}/act/${actNum}/save`, { telemetry: payload });
                setSavingState("saved");
                setTimeout(() => setSavingState("idle"), 1200);
            } catch {
                setSavingState("error");
            }
        }, 1500);
        return () => clearTimeout(t);
    }, [responses, actState.locked, actNum, state.token]);

    const handleChange = useCallback((field, text, tele) => {
        setResponses((r) => ({ ...r, [field.id]: text }));
        telemetryRef.current[field.id] = tele;
    }, []);

    const submit = async () => {
        if (submitting) return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            await axios.post(`${API_URL}/assessment/hos/${state.token}/act/${actNum}/submit`, { telemetry: Object.values(telemetryRef.current) });
            telemetryRef.current = {};
            setResponses({});
            onAdvance && onAdvance();
        } catch (e) {
            setSubmitError(e.response?.data?.detail || "Submit failed.");
            setSubmitting(false);
        }
    };

    const warnAt = section?.timer?.warning_at_seconds || 60;
    const urgent = secsLeft <= warnAt;
    const expired = secsLeft <= 0;
    const sceneItems = items.filter(i => i.type === "scenario_brief");
    const respItems = items.filter(i => i.type === "structured_response");
    const title = section?.title || ACT_TITLE_FALLBACK[actNum];

    return (
        <div className="min-h-screen bg-background" data-testid={`hos-act-${actNum}`}>
            <header className="border-b border-border/40 bg-card sticky top-0 z-20">
                <div className="max-w-3xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] font-mono text-gray-500">Act {actNum} of 5</p>
                        <h1 className="text-sm font-display font-bold text-white">{title}</h1>
                    </div>
                    <div className="flex items-center gap-4">
                        {savingState === "saving" && <span className="text-[11px] text-gray-500 flex items-center gap-1 font-mono"><Loader2 className="w-3 h-3 animate-spin" />Saving</span>}
                        {savingState === "saved" && <span className="text-[11px] text-emerald-300 flex items-center gap-1 font-mono"><Save className="w-3 h-3" />Saved</span>}
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 border ${expired ? "border-red-500/60 text-red-300" : urgent ? "border-amber-500/60 text-amber-200" : "border-border/60 text-gray-300"}`} data-testid={`hos-clock-act-${actNum}`}>
                            <Clock className="w-3.5 h-3.5" />
                            <span className="text-sm font-mono tabular-nums">{fmtClock(secsLeft)}</span>
                        </div>
                    </div>
                </div>
                <div className="h-0.5 bg-border/40">
                    <div className={`h-full transition-all duration-700 ${expired ? "bg-red-400" : urgent ? "bg-amber-300" : "bg-white"}`}
                         style={{ width: `${Math.min(100, Math.max(0, (secsLeft / Math.max(1, (actState.duration_seconds || 360))) * 100))}%` }} />
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-8">
                {expired && !actState.locked && (
                    <div className="border border-red-500/60 bg-red-500/5 px-4 py-3 mb-6 flex items-start gap-3" data-testid={`hos-act-expired-${actNum}`}>
                        <AlertTriangle className="w-4 h-4 text-red-300 mt-0.5" />
                        <div>
                            <p className="text-xs text-red-200 font-semibold">Time is up for this act.</p>
                            <p className="text-[11px] text-red-200/70">Submit now to capture what you have.</p>
                        </div>
                    </div>
                )}

                {sceneItems.map((sb, i) => (
                    <article key={sb.id} className="bg-card border border-border/40 p-5 mb-8" data-testid={`hos-scenario-${sb.id}`}>
                        <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-gray-500 mb-2">{sb.title || "Situation"}</p>
                        <p className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap">{sb.content}</p>
                    </article>
                ))}

                <div className="space-y-7">
                    {respItems.map((q) => (
                        <article key={q.id} className="bg-card border border-border/40 p-5" data-testid={`hos-response-${q.id}`}>
                            <p className="text-[10px] uppercase tracking-[0.2em] font-mono text-gray-500 mb-1">{q.title}</p>
                            <p className="text-sm text-gray-300 leading-relaxed mb-4">{q.prompt}</p>
                            <TAResponseField
                                field={{ id: q.id, label: q.title, type: "long_text", max_words: q.max_words }}
                                itemId={q.id}
                                value={responses[q.id] || ""}
                                initialTelemetry={telemetryRef.current[q.id]}
                                onChange={(text, tele) => handleChange({ id: q.id }, text, tele)}
                                readOnly={submitting}
                                language="plaintext"
                            />
                        </article>
                    ))}
                </div>

                {submitError && (
                    <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 my-6">
                        <p className="text-xs text-red-300" data-testid={`hos-submit-error-${actNum}`}>{submitError}</p>
                    </div>
                )}

                <div className="sticky bottom-4 mt-12 bg-card border border-border/40 px-5 py-4 flex items-center justify-between">
                    <p className="text-[11px] text-gray-500 leading-relaxed max-w-md">
                        Submitting locks Act {actNum}. {actNum < 5 ? `Act ${actNum + 1}'s clock will start immediately.` : "This is the final act."}
                    </p>
                    <button onClick={submit} disabled={submitting || actState.locked}
                        className="px-5 py-2.5 bg-white text-black text-sm font-semibold hover:bg-gray-200 disabled:opacity-50 disabled:cursor-wait flex items-center gap-2"
                        data-testid={`hos-submit-act-${actNum}-btn`}>
                        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Submitting…</> : <><Send className="w-4 h-4" />{actNum < 5 ? `Submit Act ${actNum}` : "Submit final act"}</>}
                    </button>
                </div>
            </main>
        </div>
    );
}
